import { combineReducers, compose } from '@reduxjs/toolkit';

import slice from './checkout.slice';

import { notNull } from 'utils/type_guards';

import { braintreeClient } from 'domains/braintree_client';
import cartModule from 'domains/cart';
import cartBilling from 'domains/cart_billing';
import cartShipping from 'domains/cart_shipping';
import cartPaymentMethod from 'domains/cart_payment_method';
import giftCert from 'domains/gift_cert';

import type { CheckoutRootState } from 'store/checkout_store';
import type { CartSubmitData } from './checkout.types';

/** Represents the slice as it exists in the final store's reducer */
const combined = combineReducers({ [slice.name]: slice.reducer });
type GlobalState = ReturnType<typeof combined>;

/** Get the local state for the current domain */
const localState = (state: GlobalState) => state[slice.name];
type LocalState = ReturnType<typeof localState>;

const selectCheckoutInitialized = (state: LocalState) => state.initialized;
const selectCheckoutSubmitStatus = (state: LocalState) => state.submitStatus;
const selectCheckoutSubmitError = (state: LocalState) => state.submitError;
const selectConfirmationDetailsStatus = (state: LocalState) => state.confirmationDetails.status;

/**
 * Does the cart have all the correct requirements for submitting
 */
const selectHasFullyQualifiedCart = (state: CheckoutRootState) => {
  const cart = cartModule.selectors.cart(state);
  const hasValidPayment = cartPaymentMethod.selectors.hasValidType(state);
  const cert = giftCert.selectors.valid(state);

  return (
    notNull(cart) &&
    notNull(cart.shipping?.shipping_method_api_id) &&
    hasValidPayment &&
    // We should have the valid gift cert available if one is being used
    ((cart.gift_certificate.id && !!cert) || !cart.gift_certificate.id)
  );
};

/**
 * Expected values to pass when submitting an order
 */
const selectSubmitOrderValues = (state: CheckoutRootState): CartSubmitData => {
  const billing = cartBilling.selectors.billing(state);
  const total = billing?.total;
  const giftCertUsage = cartPaymentMethod.selectors.giftCert(state);

  const token = cartPaymentMethod.selectors.token(state);
  const paymentNonce = token?.nonce;

  const paymentType = cartPaymentMethod.selectors.validType(state);
  if (!notNull(total)) {
    throw new Error('No Billing Data');
  }

  if (!notNull(paymentType)) {
    throw new Error('No Payment Method');
  }

  const submitData: CartSubmitData = {
    expected_total: total,
    expected_gift_certificate_usage_total: giftCertUsage.usage,
    payment_type: paymentType,
  };

  if (paymentType === 'credit_card') {
    if (!paymentNonce) throw new Error('No Payment Nonce');
    submitData.braintree_device_data = braintreeClient.deviceData;
    submitData.braintree_payment_method_nonce = paymentNonce;
  }

  return submitData;
};

const selectCheckoutHasError = (state: CheckoutRootState) =>
  selectConfirmationDetailsStatus(localState(state)) === 'error' ||
  cartShipping.selectors.methodStatus(state) === 'error' ||
  cartPaymentMethod.selectors.status(state) === 'error' ||
  cartModule.selectors.status(state) === 'error';

export default {
  isInitialized: compose(selectCheckoutInitialized, localState),
  submitStatus: compose(selectCheckoutSubmitStatus, localState),
  submitError: compose(selectCheckoutSubmitError, localState),
  confirmationDetailsStatus: compose(selectConfirmationDetailsStatus, localState),
  // Already globalized
  hasError: selectCheckoutHasError,
  submitValues: selectSubmitOrderValues,
  hasFullyQualifiedCart: selectHasFullyQualifiedCart,
};
